/**
 * Layer navigation toolbar - renders the header from the layer registry.
 *
 * The registry (layers-core.js) says WHAT each layer has; this module is the
 * only place that turns that into DOM. Adding a layer is an entry there, not
 * markup here. See docs/systemsRework/LayerNavigation.md.
 *
 * Two render paths on purpose:
 *
 *   renderLayerToolbar()   full structural rebuild - on boot, layer switch, and
 *                          unlock changes. Replaces DOM.
 *   refreshToolbarValues() patches text only - safe to call every second.
 *
 * The split matters: the energy popup is anchored to the ⚡ chip and closes on
 * an outside click, so rebuilding chips underneath it once a second would tear
 * it out from under the player mid-purchase. Values change constantly;
 * structure changes rarely.
 */

import {
  getLayers, getLayer, currentLayerId, setCurrentLayer,
  screensFor, currenciesFor, activeTaskFor, layersNeedingAttention,
} from './layers-core.js';
import { debug, CH } from './debugLog.js';

// Last screen visited per layer, so returning to Command lands where you left
// it. This is what makes switching cheap rather than punishing.
const lastScreenByLayer = new Map();

/**
 * The screen the player is on (or heading to), as a `data-tab` value.
 *
 * This is INTENT, deliberately not derived from `.tab-content.active`. Tab
 * switching is animated: the outgoing pane keeps `.active` until its fade-out
 * ends, so anything reading the DOM synchronously after a click sees the
 * PREVIOUS screen. That made the toolbar paint the wrong button active, and
 * because the click handler skips buttons already marked active, the stale
 * class then swallowed the next navigation to that screen.
 */
let currentScreenTab = null;

/** @returns {string|null} the current screen's data-tab value. */
export function currentScreen() {
  return currentScreenTab;
}

// Below this width screen tabs drop their labels and show icons only. The layer
// name already appears in the switcher, so screen tabs carry less naming burden
// and are the right thing to shed labels first.
const ICON_ONLY_MAX_WIDTH = 900;

const isNarrow = () => window.innerWidth <= ICON_ONLY_MAX_WIDTH;

const BTN_BASE = 'tab-btn font-bold py-1 rounded-lg border-2 hover:shadow-lg whitespace-nowrap';

/**
 * Horizontal padding for every toolbar button, tightened on narrow viewports.
 *
 * The toolbar's width budget is genuinely tight — Field measured 753 of 768 —
 * and a third Syndicate screen (Charter / Operations / Stores) pushed it 34px
 * over, silently clipping the help button off the right edge. One uniform rule
 * beats shaving a label: it recovers the room from the whole bar rather than
 * from whichever control was added last, and it leaves headroom for the next
 * screen instead of paying this cost again.
 */
const btnBase = () => `${BTN_BASE} ${isNarrow() ? 'px-1.5' : 'px-3'}`;

/**
 * Active-task button styling, keyed by task state.
 *
 * The rule the header reads by: **solid means act, quiet means FYI.** A player
 * scanning the toolbar should be able to tell "you owe me an input" from "this
 * is running fine" without reading the label.
 *
 * `complete` deliberately mirrors the in-game raid button's own "Complete Raid"
 * styling (`raid.js` updateRaidButtonStates) - same gradient, same foil. It is
 * the same action on two surfaces, so it should not be a different colour on
 * each; it previously rendered `bg-green-400/10`, which is 10% green over a
 * dark header and reads as near-black.
 *
 * `bg-amber-* text-black` is the idiom used everywhere else in the app for a
 * solid amber control. The raid button reaches the same place by a different
 * route - `.start-raid-btn` sets #1a1a2e in CSS - and the difference is not
 * perceptible on amber, so this stays with the Tailwind idiom.
 *
 * `running` is the only state with nothing to do, so it is the only quiet one.
 * That contrast is what gives the solid states meaning - if every state were
 * solid, "solid" would signal nothing.
 */
const TASK_TONES = {
  // Finished, awaiting collection - the strongest call to action.
  complete: 'border-amber-300 text-black bg-gradient-to-r from-amber-500 to-amber-600',
  // Startable now.
  idle: 'border-amber-400 text-black bg-amber-600 hover:bg-amber-500',
  // In progress - informational, nothing for the player to do.
  running: 'border-amber-800/70 text-amber-100/80 bg-white/5',
  // A higher layer runs this loop hands-off (L3+): also nothing to do.
  automated: 'border-sky-800/70 text-sky-100/80 bg-white/5',
};

function el(tag, className, html) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (html != null) node.innerHTML = html;
  return node;
}

/** The layer switcher - shows where you are, and badges when elsewhere needs you. */
function renderSwitcher(container, layer) {
  container.replaceChildren();

  const btn = el('button', `${btnBase()} relative bg-white/5`);
  btn.id = 'layer-switcher-btn';
  btn.style.borderColor = layer.accent;
  btn.style.color = layer.accent;
  btn.title = `Current layer: ${layer.label}`;
  btn.innerHTML = `${layer.icon} ${layer.label} <span class="opacity-70">▾</span>`;

  // Badge counts LAYERS needing attention, not tasks, so it stays stable as
  // layers and task counts grow. Neutral colour: "something needs you" must
  // never read as a layer identity.
  const waiting = layersNeedingAttention(layer.id);
  if (waiting.length > 0) {
    const badge = el('span',
      'absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 ' +
      'text-black text-[10px] font-extrabold grid place-items-center',
      String(waiting.length));
    badge.title = `Needs attention: ${waiting.join(', ')}`;
    btn.appendChild(badge);
  }

  btn.addEventListener('click', (e) => { e.stopPropagation(); toggleLayerPanel(); });
  container.appendChild(btn);
}

/** The current layer's screens. Never the union - one layer's context at a time. */
function renderScreens(container, layer) {
  container.replaceChildren();
  // Intent, not `.tab-content.active` — see currentScreenTab.
  const activeTab = currentScreenTab;
  const narrow = isNarrow();

  // screensFor() applies each screen's `visible` predicate — do not read
  // layer.screens directly, or hidden screens come back on every render.
  for (const screen of screensFor(layer.id)) {
    const isActive = screen.tab === activeTab;
    const btn = el('button', `${btnBase()} ${isActive ? 'active' : ''} text-gray-300 bg-white/5`);
    btn.dataset.tab = screen.tab;
    btn.style.borderColor = isActive ? layer.accent : '';
    btn.title = screen.label;
    btn.textContent = narrow ? screen.icon : screen.label;
    if (!isActive) btn.classList.add('border-gray-500');
    container.appendChild(btn);
  }
}

/**
 * The active-task slot - shortcut to *and* live status of this layer's task.
 * Replaces the hardcoded "New Raid" button: raiding is Field's task, not a
 * privileged global one.
 */
function renderTask(container, layer) {
  container.replaceChildren();
  const task = activeTaskFor(layer.id);
  if (task.state === 'none') return; // spec: hide the slot rather than show a dead control

  const btn = el('button', `${btnBase()} ${TASK_TONES[task.state] ?? TASK_TONES.idle}`);
  btn.id = 'layer-task-btn';
  btn.dataset.tab = task.screen;
  // Stamped here, not only in refreshToolbarValues: this is what the tick
  // compares against to decide whether a structural rebuild is needed. Leaving
  // it unset meant any external renderLayerToolbar() call looked like a state
  // change on the next tick, forcing a rebuild that closed an open panel.
  btn.dataset.state = task.state;
  // Optional: a task can ask for a particular VIEW of its screen. Syndicate's
  // Operations screen is both a planner and a transit board, and the toolbar
  // button means "plan a new one" while the screen tab means "show me what is
  // running". The tab handler reads this off the clicked element.
  if (task.mode) btn.dataset.mode = task.mode;
  btn.textContent = task.label;
  if (task.state === 'complete') btn.classList.add('foil-card');
  container.appendChild(btn);
}

/**
 * Currencies for this layer only - NOT accumulated. Credits and energy are not
 * actionable in Command, so they do not appear there. This is the single change
 * that stops the right-hand cluster growing once per layer, and it is most of
 * the portrait-clipping fix.
 */
function renderCurrencies(container, layer) {
  container.replaceChildren();

  for (const currency of currenciesFor(layer.id)) {
    const chip = el('div',
      'flex items-center gap-1 px-2 py-1 bg-white/10 rounded-lg border-2 border-gray-900');
    chip.id = `${currency.id}-chip`;
    if (currency.exact) chip.title = currency.exact;
    chip.appendChild(el('span', null, currency.icon));

    const value = el('span', 'font-bold');
    value.id = `currency-${currency.id}`;
    value.textContent = currency.value;
    value.style.color = currency.id === 'energy' ? '#60a5fa' : layer.accent;
    chip.appendChild(value);

    // Energy carries a purchase affordance. The popup itself lives outside the
    // chips (see index.html) so re-rendering currencies cannot destroy it.
    if (currency.id === 'energy') {
      const buy = el('button',
        'ml-1 px-1.5 rounded font-bold text-blue-300 bg-blue-900/60 border border-blue-700 ' +
        'hover:bg-blue-700 hover:text-white transition-colors', '+');
      buy.id = 'buy-energy-btn';
      buy.title = 'Buy energy';
      buy.addEventListener('click', (e) => window.toggleEnergyPopup?.(e));
      chip.appendChild(buy);
    }
    container.appendChild(chip);
  }
}

/** Full structural rebuild. Boot, layer switch, unlock change, breakpoint cross. */
export function renderLayerToolbar() {
  const layer = getLayer(currentLayerId());
  if (!layer) return;

  const switcher = document.getElementById('layer-switcher');
  const screens = document.getElementById('layer-screens');
  const task = document.getElementById('layer-task');
  const currencies = document.getElementById('layer-currencies');
  if (!switcher || !screens || !task || !currencies) return; // header not on this page

  // A structural rebuild replaces the switcher button the panel is anchored to
  // and whose outside-click handler references it, so an open panel cannot
  // survive one. Rare in practice — this fires on layer switch, unlock, and
  // task-state change, not on the per-second tick.
  if (panelIsOpen()) closeLayerPanel();

  renderSwitcher(switcher, layer);
  renderScreens(screens, layer);
  renderTask(task, layer);
  renderCurrencies(currencies, layer);
}

/**
 * Patch values without touching structure. Safe on a timer - see the module
 * header for why that distinction exists.
 */
export function refreshToolbarValues() {
  const layerId = currentLayerId();

  for (const currency of currenciesFor(layerId)) {
    const node = document.getElementById(`currency-${currency.id}`);
    if (node) node.textContent = currency.value;
    // Patched here too, not only on a structural rebuild: an abbreviated chip
    // holds the exact figure in its tooltip, and credits change every raid
    // while structure does not. Missing this would pin the tooltip to whatever
    // the balance was at the last rebuild.
    const chip = document.getElementById(`${currency.id}-chip`);
    if (chip) {
      if (currency.exact) chip.title = currency.exact;
      else chip.removeAttribute('title');
    }
  }

  // The task's LABEL changes constantly (raid progress); its presence and tone
  // change rarely. Rebuild only when the state actually changes.
  const task = activeTaskFor(layerId);
  const btn = document.getElementById('layer-task-btn');
  const stateChanged = (btn?.dataset.state ?? 'none') !== task.state;
  if (stateChanged) {
    renderLayerToolbar(); // renderTask stamps the new state
    return;
  }
  if (btn) btn.textContent = task.label;

  // A layer unlocking (or a raid completing elsewhere) changes the badge.
  const expected = layersNeedingAttention(layerId).length;
  const shown = document.querySelector('#layer-switcher .bg-amber-500');
  if ((expected > 0) !== !!shown) renderLayerToolbar();
}

/**
 * Move to a layer and land on the screen last used there.
 *
 * Dispatches a click on the screen's tab button rather than duplicating the
 * switch: main.js owns tab activation (animation, per-tab refreshes), and
 * re-implementing it here would be a second copy to keep in step.
 */
export function switchLayer(layerId) {
  if (!setCurrentLayer(layerId)) return false;

  const layer = getLayer(layerId);
  const remembered = lastScreenByLayer.get(layerId);
  // Only currently-visible screens are valid targets, or we would try to click
  // a button that was never rendered.
  const visible = screensFor(layerId);
  const target = visible.find(s => s.tab === remembered) || visible[0];

  // Render first so the new layer's screen buttons exist to be clicked.
  renderLayerToolbar();
  if (target) {
    if (target.tab === currentScreenTab) {
      // Already displaying the target screen. The click handler would treat
      // this as a no-op and return, leaving the toolbar unpainted, so repaint
      // directly. Cannot happen while layers own distinct screens, but the
      // registry does not guarantee that.
      renderLayerToolbar();
    } else {
      document.querySelector(`#layer-screens [data-tab="${target.tab}"]`)?.click();
    }
  }
  debug(CH.ui, `switched to layer ${layerId}`);
  return true;
}

// --- Switcher panel -------------------------------------------------------
// The badge says *something* needs you; the panel says *which* layer. That
// split is why the badge can stay a stable count of layers rather than trying
// to describe tasks.

let closePanelOnOutside = null;

/** Is the panel currently open? */
function panelIsOpen() {
  return !document.getElementById('layer-panel')?.classList.contains('hidden');
}

/**
 * One row per layer, in ladder order.
 *
 * Locked layers are shown dimmed with a lock rather than hidden — a prestige
 * ladder should advertise what is ahead (spec open question 1). They render
 * from the same registry list, so a new layer appears here by existing.
 */
function renderLayerPanel() {
  const panel = document.getElementById('layer-panel');
  if (!panel) return;
  panel.replaceChildren();

  const activeId = currentLayerId();

  for (const layer of getLayers()) {
    const unlocked = layer.unlocked();
    const isCurrent = layer.id === activeId;
    const task = unlocked ? activeTaskFor(layer.id) : { state: 'none', label: '' };
    const needsAttention = task.state === 'complete';

    const row = document.createElement('button');
    row.className =
      'w-full flex items-center gap-2 px-2 py-2 mb-1 rounded-lg border-2 text-left ' +
      (unlocked ? 'hover:bg-white/10 ' : 'opacity-40 cursor-not-allowed ');
    row.disabled = !unlocked;
    row.dataset.layer = layer.id;
    // Current layer wears its own accent; a layer asking for attention is
    // outlined in the neutral alert colour so "needs you" never reads as an
    // identity. Everything else stays quiet.
    row.style.borderColor = needsAttention ? '#f59e0b' : (isCurrent ? layer.accent : 'transparent');

    const icon = el('span', 'text-lg', unlocked ? layer.icon : '🔒');
    const label = el('span', 'font-bold flex-1');
    label.textContent = layer.label;
    if (unlocked) label.style.color = isCurrent ? layer.accent : '#d1d5db';
    row.append(icon, label);

    if (needsAttention) {
      row.appendChild(el('span',
        'text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-black whitespace-nowrap',
        task.label));
    } else if (isCurrent) {
      row.appendChild(el('span', 'text-[10px] text-gray-500', 'here'));
    }

    if (unlocked && !isCurrent) {
      row.addEventListener('click', () => { closeLayerPanel(); switchLayer(layer.id); });
    } else if (isCurrent) {
      row.addEventListener('click', closeLayerPanel);
    }
    panel.appendChild(row);
  }
}

/** Open the panel under the switcher. */
export function openLayerPanel() {
  const panel = document.getElementById('layer-panel');
  const anchor = document.getElementById('layer-switcher-btn');
  if (!panel || !anchor) return;

  renderLayerPanel();
  panel.classList.remove('hidden');

  const rect = anchor.getBoundingClientRect();
  panel.style.top = `${rect.bottom + 8}px`;
  panel.style.left = `${Math.max(8, rect.left)}px`;

  // Deferred so the click that opened it does not immediately close it.
  closePanelOnOutside = (e) => {
    if (!panel.contains(e.target) && e.target !== anchor) closeLayerPanel();
  };
  setTimeout(() => document.addEventListener('click', closePanelOnOutside), 0);
  document.addEventListener('keydown', closeOnEscape);
}

export function closeLayerPanel() {
  const panel = document.getElementById('layer-panel');
  if (!panel) return;
  panel.classList.add('hidden');
  if (closePanelOnOutside) {
    document.removeEventListener('click', closePanelOnOutside);
    closePanelOnOutside = null;
  }
  document.removeEventListener('keydown', closeOnEscape);
}

function closeOnEscape(e) {
  if (e.key === 'Escape') closeLayerPanel();
}

/** Switcher click: open the panel, or close it if already open. */
export function toggleLayerPanel() {
  if (panelIsOpen()) closeLayerPanel();
  else openLayerPanel();
}

/**
 * Record where the player has navigated.
 *
 * Always updates the current screen (including targets that are not screen tabs,
 * such as the raid the active-task button jumps to) so the toolbar and the
 * click guard agree on where we are. Only screens belonging to the current
 * layer are remembered for return-to-where-you-left.
 */
export function noteScreenVisited(tabName) {
  if (!tabName) return;
  currentScreenTab = tabName;
  const layerId = currentLayerId();
  if (getLayer(layerId)?.screens.some(s => s.tab === tabName)) {
    lastScreenByLayer.set(layerId, tabName);
  }
}

/** Wire the toolbar up. Called once from main.js on DOMContentLoaded. */
export function initLayerNav() {
  // Seed the current screen from the DOM. Safe here and only here: at boot
  // nothing is mid-animation, so `.tab-content.active` is the truth rather
  // than a lagging snapshot. Falls back to the layer's first screen.
  const active = document.querySelector('.tab-content.active')?.id?.replace(/-tab$/, '');
  noteScreenVisited(active || getLayer(currentLayerId())?.screens[0]?.tab);

  renderLayerToolbar();

  // Crossing the icon-only breakpoint changes structure, not just values.
  let wasNarrow = isNarrow();
  window.addEventListener('resize', () => {
    if (isNarrow() !== wasNarrow) {
      wasNarrow = isNarrow();
      renderLayerToolbar();
    }
  });
}

// Exposed for the same reason the rest of the game's handlers are: inline
// onclick attributes and cross-module calls without an import cycle.
if (typeof window !== 'undefined') {
  window.renderLayerToolbar = renderLayerToolbar;
  window.switchLayer = switchLayer;
  window.toggleLayerPanel = toggleLayerPanel;
}
